import Api from "api/Api";
import type { PACKAGE_TYPE } from "ee/constants/PackageConstants";
import type { Package } from "ee/constants/PackageConstants";
import type { ApiResponse } from "api/ApiResponses";
import type { AxiosPromise } from "axios";
import type {
  Module,
  ModuleMetadata,
  PACKAGE_PULL_STATUS,
} from "ee/constants/ModuleConstants";
import type {
  DeletePackagePayload,
  PublishPackagePayload,
  FetchConsumablePackagesInWorkspacePayload,
  PullPackagePayload,
} from "ee/actions/packageActions";
import type { Datasource } from "entities/Datasource";
import type { ModuleInstance } from "ee/constants/ModuleInstanceConstants";
import type { FetchModuleInstanceEntitiesResponse } from "./ModuleInstanceApi";
import type { AppTheme } from "entities/AppTheming";

export interface CreatePackagePayload {
  workspaceId: string;
  name: string;
  icon?: string;
  color?: string;
  type?: PACKAGE_TYPE;
}

export interface CreateUIPackageWithModulePayload {
  workspaceId: string;
  packageData: Omit<CreatePackagePayload, "workspaceId">;
  defaultModule: Omit<Module, "packageId">;
}

export interface FetchPackagePayload {
  basePackageId: string;
  branchName?: string;
}

export interface FetchPackageResponse {
  packageData: Package;
  modules: Module[];
  modulesMetadata: ModuleMetadata[];
  systemThemes: AppTheme[];
}

export interface FetchConsumablePackagesInWorkspaceResponse {
  packages: Package[];
  modules: Module[];
  moduleMetadata: ModuleMetadata[];
}

export interface PullPackageResponse {
  consumables: FetchConsumablePackagesInWorkspaceResponse;
  moduleInstances: ModuleInstance[];
  entities: FetchModuleInstanceEntitiesResponse;
}

export interface ImportPackagePayload {
  workspaceId: string;
  file?: File;
  packageId?: string;
}

export interface ImportPackageResponse {
  package: Package;
  isPartialImport: boolean;
  unConfiguredDatasourceList: Datasource[];
}

export interface FetchPackagesByWorkspacePayload {
  workspaceId: string;
}

export interface PublishPackageResponse {
  status: boolean;
  lastPublishedVersion: string;
}

export interface PollPackagePullStatusApiPayload {
  applicationId: string;
}

export type PollPackagePullStatusResponse = PACKAGE_PULL_STATUS;

export interface ChangeConsumedPackageVersionPayload {
  branchedApplicationId: string;
  requestPageId: string;
  packageUUID: string;
  version: string;
}

export interface ChangeConsumedPackageVersionResponse {
  consumables: FetchConsumablePackagesInWorkspaceResponse;
  moduleInstances: ModuleInstance[];
  entities: FetchModuleInstanceEntitiesResponse;
}

const BASE_URL = "v1/packages";
const PACKAGE_PUBLISH_TIMEOUT = 60000; // in ms
const PACKAGE_PULL_TIMEOUT = 60000; // in ms

class PackageApi extends Api {
  static async fetchAllPackages() {
    const url = `${BASE_URL}`;

    return Api.get(url);
  }

  static async fetchPackagesByWorkspace(
    payload: FetchPackagesByWorkspacePayload,
  ): Promise<AxiosPromise<ApiResponse<Package[]>>> {
    const url = BASE_URL;

    return Api.get(url, payload);
  }

  static async fetchConsumablePackagesInWorkspace(
    payload: FetchConsumablePackagesInWorkspacePayload,
  ): Promise<
    AxiosPromise<ApiResponse<FetchConsumablePackagesInWorkspaceResponse>>
  > {
    const url = `${BASE_URL}/consumables?branchedApplicationId=${payload.applicationId}`;

    return Api.get(url);
  }

  static async createPackage(
    payload: CreatePackagePayload,
  ): Promise<AxiosPromise<ApiResponse<Package>>> {
    const url = BASE_URL;
    const { workspaceId, ...body } = payload;
    const queryParams = { workspaceId };

    return Api.post(url, body, queryParams);
  }

  static async fetchPackage(
    payload: FetchPackagePayload,
  ): Promise<AxiosPromise<ApiResponse<FetchPackageResponse>>> {
    const { basePackageId, branchName } = payload;
    const url = `${BASE_URL}/${basePackageId}`;
    const params = { refName: branchName };

    return Api.get(url, undefined, { params });
  }

  static async pullPackage(
    payload: PullPackagePayload,
  ): Promise<AxiosPromise<ApiResponse<PullPackageResponse>>> {
    const { applicationId, pageId } = payload;
    const url = `${BASE_URL}/${applicationId}/pull`;

    const queryParams = {
      requestPageId: pageId,
    };

    return Api.post(url, null, queryParams, {
      timeout: PACKAGE_PULL_TIMEOUT,
    });
  }

  static async pollPackagePullStatus(
    payload: PollPackagePullStatusApiPayload,
  ): Promise<AxiosPromise<ApiResponse<PollPackagePullStatusResponse>>> {
    const { applicationId } = payload;
    const url = `${BASE_URL}/${applicationId}/poll-pull-status`;

    return Api.get(url);
  }

  static async updatePackage(
    payload: Package,
  ): Promise<AxiosPromise<ApiResponse<Package>>> {
    const url = `${BASE_URL}/${payload.id}`;

    return Api.put(url, payload);
  }

  static async deletePackage(
    payload: DeletePackagePayload,
  ): Promise<AxiosPromise<ApiResponse<Package>>> {
    const url = `${BASE_URL}/${payload.id}`;

    return Api.delete(url);
  }

  static async publishPackage(
    payload: PublishPackagePayload,
  ): Promise<AxiosPromise<ApiResponse<PublishPackageResponse>>> {
    const { packageId, ...params } = payload;
    const url = `${BASE_URL}/${packageId}/publish`;

    return Api.post(url, params, undefined, {
      timeout: PACKAGE_PUBLISH_TIMEOUT,
    });
  }

  static async importPackage(
    payload: ImportPackagePayload,
  ): Promise<AxiosPromise<ApiResponse<ImportPackageResponse>>> {
    const formData = new FormData();
    const { file, packageId, workspaceId } = payload;

    if (file) {
      formData.append("file", file);
    }

    const url = `${BASE_URL}/import/${workspaceId}`;
    const queryParams: Record<string, string> = {};

    if (packageId) {
      queryParams.packageId = packageId;
    }

    return Api.post(url, formData, queryParams, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  static async changeConsumedPackageVersion(
    payload: ChangeConsumedPackageVersionPayload,
  ) {
    const { branchedApplicationId, requestPageId, ...body } = payload;
    const url = `${BASE_URL}/${branchedApplicationId}/change-version`;

    return Api.post(url, body, {
      requestPageId,
    });
  }

  static async createPackageWithModule(
    payload: CreateUIPackageWithModulePayload,
  ): Promise<AxiosPromise<ApiResponse<Package>>> {
    const url = `${BASE_URL}/with-module`;
    const { workspaceId, ...body } = payload;
    const queryParams = { workspaceId };

    return Api.post(url, body, queryParams);
  }
}

export default PackageApi;
